import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import AccordionItem from 'components/common/AccordionItem';
import { useDispatch, useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import { useTranslation } from 'react-i18next';
import { Submission } from 'pages/ConductInspection/components/Submission';
import { ChecklistDetails } from 'pages/ConductInspection/components/ChecklistDetails';
import { ProjectDetails } from 'pages/ConductInspection/components/ProjectDetails';
import { useSetDropdownAndRole } from 'hooks/useSetDropdownAndRole';
import { Loader } from 'components/common/Loader';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useGetInspectionByIdMutation,
  useGetReportMutation,
  useGetTasksByTypeMutation,
} from 'redux/services/inspection';
import { getProjectDetailsFromInspectionData } from 'pages/utils';
import {
  IInspectionReport,
  InspectionTask,
  setChecklistDetails,
  setProjectDetails,
  setSubmissionDetails,
} from 'redux/conductInspectionSlice';
import { useGetChecklist } from 'hooks/useGetChecklist';
import { Button } from '@mui/material';
import {
  Chat,
  ArrowBack,
  TaskAlt,
  PendingActions,
  FileDownload,
} from '@mui/icons-material';
import { ChatConversation } from './ChatConversation';
import { CommentItem } from 'components/ChatComponents/CommentItem';
import './ReviewInspection.scss';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { downloadInspectionReport } from './downloadUtils';
import toast from 'react-hot-toast';

const ReviewInspection: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [firstLoad, setFirstLoad] = useState(false);
  const [searchParams] = useSearchParams();
  const itemRefs = useRef<any>([]);
  const [selectedTask, setSelectedTask] = useState<InspectionTask | null>(null);

  const { projectId, inspectionId } = useParams();
  const [
    getAllInspectionDetails,
    { data: inspection, isLoading: isInspectionLoading },
  ] = useGetInspectionByIdMutation();
  const [getTasks, { data: tasks }] = useGetTasksByTypeMutation();
  const [getReport] = useGetReportMutation();

  const checklist = useGetChecklist();

  const { project } = useSetDropdownAndRole();

  useEffect(() => {
    const taskId = searchParams.get('taskId');

    if (
      taskId &&
      project &&
      !firstLoad &&
      inspection &&
      tasks?.length > 0 &&
      selectedTask &&
      !isInspectionLoading
    ) {
      setTimeout(() => {
        const taskIndex = tasks.findIndex(
          (t: InspectionTask) => t._id === taskId,
        );
        itemRefs.current?.[taskIndex]?.scrollIntoView({
          behavior: 'smooth',
        });
      }, 500);

      setFirstLoad(true);
    }
  }, [
    searchParams,
    inspection,
    project,
    tasks,
    firstLoad,
    selectedTask,
    isInspectionLoading,
  ]);

  useEffect(() => {
    return () => {
      dispatch(setProjectDetails(null));
      dispatch(setChecklistDetails(null));
      dispatch(setSubmissionDetails(null));
      setSelectedTask(null);
    };
  }, [dispatch]);

  useEffect(() => {
    if (project && checklist && inspection) {
      dispatch(
        setProjectDetails(
          getProjectDetailsFromInspectionData(inspection, checklist, project),
        ),
      );
      dispatch(setChecklistDetails(inspection.response));
      dispatch(
        setSubmissionDetails({
          submissionDocuments: inspection.submissionDocuments ?? [],
          drawingTitle: inspection.drawingTitle ?? [],
          doerImages: inspection.doerImages ?? [],
          generalFeedback: inspection.generalFeedback,
          satisfactionLevel: inspection.satisfactionLevel,
          eSign: inspection.eSign,
          status: inspection.status,
          closeByDate: inspection.closeByDate,
        }),
      );
    }
  }, [project, checklist, inspection, inspectionId, dispatch]);

  const fetchInspection = useCallback(async () => {
    await getAllInspectionDetails({
      projectId,
      id: inspectionId,
    });
  }, [getAllInspectionDetails, inspectionId, projectId]);

  useEffect(() => {
    if (inspectionId && !inspection) {
      fetchInspection();
      getTasks({
        projectId,
        id: inspectionId,
      });
    }
  }, [fetchInspection, inspectionId, inspection, getTasks, projectId]);

  useEffect(() => {
    const taskId = searchParams.get('taskId');

    if (tasks && (selectedTask || taskId)) {
      const task =
        tasks.find(
          (t: InspectionTask) => t._id === (taskId || selectedTask?._id),
        ) ?? null;

      setSelectedTask(task);
    }
  }, [searchParams, selectedTask, tasks]);

  const selectedChecklist = useSelector(
    (state: RootStates) =>
      state.conductInspection.projectDetails?.selectedChecklist,
  );

  const getViewTaskDetailsHandler = (task: InspectionTask) => () => {
    setFirstLoad(true);
    setSelectedTask(task);
    navigate(
      `/project/${projectId}/inspection/${inspection._id}/review?taskId=${task._id}`,
    );
  };

  const handleDrawerClose = () => {
    setSelectedTask(null);
    const prevPath = searchParams.get('path');

    navigate(
      `/project/${projectId}/inspection/${inspection._id}/review?path=${
        prevPath || ''
      }`,
      { replace: true },
    );
  };

  const handleChatUpdate = () => {
    getTasks({
      projectId,
      id: inspectionId,
    });
  };

  const handleBack = () => {
    const prevPath = searchParams.get('path');

    dispatch(setProjectDetails(null));
    dispatch(setChecklistDetails(null));
    dispatch(setSubmissionDetails(null));
    setSelectedTask(null);
    if (prevPath) {
      navigate(prevPath, { replace: true });
    } else {
      navigate(`/project/${projectId}/inspection`, { replace: true });
    }
  };

  const handleDownload = async () => {
    toast.success('Report will be downloaded shortly');
    const res: any = await getReport({
      id: inspectionId,
    });

    if (res.data as IInspectionReport) {
      downloadInspectionReport(res.data, project?.team ?? []);
    } else {
      toast.error('Report generation failed. Please try again later');
    }
  };

  return project && !isInspectionLoading && checklist ? (
    <div className="review-inspection d-flex flex-column">
      <div className="review-sub-header p-2">
        <span className="h5">{`# ${inspection?.displayId ?? ''}`}</span>
        <span className="h5 mx-2">{t('review_inspection_sub_header')}</span>
      </div>
      <div className="d-flex flex-column w-100 h-100 p-2 flex-grow-1 overflow-auto">
        <div className="d-flex flex-column form-content">
          <Accordion
            defaultActiveKey={
              searchParams.get('taskId') ? 'reinspection_details' : '-1'
            }
          >
            <AccordionItem
              eventKey="project_details"
              text={t('label_project_details')}
              children={<ProjectDetails disabled noFooter project={project} />}
            />
            <AccordionItem
              eventKey="checklist_details"
              text={`#${selectedChecklist?.name ?? ''} ${t('checklist')}`}
              children={
                <ChecklistDetails disabled noFooter project={project} />
              }
            />
            <AccordionItem
              eventKey="submission_details"
              text={t('submission')}
              children={<Submission disabled noFooter project={project} />}
            />

            <AccordionItem
              eventKey="reinspection_details"
              text={t('label_reinspection_tags')}
              leftAction={
                tasks?.some(
                  (t: InspectionTask) => t.status === WorkFlowStatus.OPEN,
                ) ? (
                  <PendingActions className="mx-2" color="warning" />
                ) : (
                  tasks?.length > 0 && (
                    <TaskAlt className="mx-2" color="success" />
                  )
                )
              }
              children={
                tasks?.length > 0 ? (
                  <ul className="list-group list-style-none">
                    {tasks?.map((task: InspectionTask, i: number) => (
                      <li
                        key={task._id}
                        ref={(el) => (itemRefs.current[i] = el)}
                        className="list-group-item d-flex flex-column w-100"
                      >
                        <div className="d-flex my-2 flex-nowrap">
                          {task.status === WorkFlowStatus.CLOSE ? (
                            <TaskAlt className="mx-2" color="success" />
                          ) : (
                            <PendingActions className="mx-2" color="warning" />
                          )}
                          <div className="task-title overflow-hidden">
                            <span>{task.name}</span>
                          </div>
                        </div>
                        <div className="question-info d-flex flex-column">
                          <span className="question-title">
                            {task.questionInfo.question}
                          </span>

                          {task.questionInfo.comments.length > 0 && (
                            <div className="d-flex flex-column last-comment-details">
                              <CommentItem
                                comment={
                                  task.questionInfo.comments[
                                    task.questionInfo.comments.length - 1
                                  ]
                                }
                                component="div"
                                project={project}
                              />
                            </div>
                          )}
                          <div className="d-flex justify-content-end align-items-center w-100 flex-wrap">
                            <Button
                              className="ml-2 mt-2 mx-1"
                              variant="contained"
                              startIcon={<Chat sx={{ color: '#fff' }} />}
                              onClick={getViewTaskDetailsHandler(task)}
                            >
                              {t('btn_view')}
                            </Button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="h5">No tags raised</span>
                )
              }
            />
          </Accordion>
        </div>
        {selectedTask && (
          <ChatConversation
            selectedTask={selectedTask}
            project={project}
            onDrawerClose={handleDrawerClose}
            onUpdate={handleChatUpdate}
          />
        )}

        <div className="d-flex w-100 justify-content-between">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBack}
          >
            {t('btn_back')}
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownload sx={{ color: '#fff' }} />}
            onClick={handleDownload}
          >
            {t('btn_download')}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <Loader />
  );
};

export default ReviewInspection;
